import React, { useState, useEffect } from "react";
import api from "../../services/api";
import {
  Table,
  Loader,
  Center,
  Title,
  ScrollArea,
  Badge,
  Group,
  Text,
  Button,
  Modal,
  Textarea,
  Paper,
  Box,
  ThemeIcon,
  Tooltip,
  ActionIcon
} from "@mantine/core";
import { DateInput } from '@mantine/dates';
import { useDisclosure } from "@mantine/hooks";
import { 
  IconRefresh, 
  IconClipboardCheck, // Ikon Monitoring
  IconBuilding,
  IconChefHat,
  IconPencil,
  IconPlus,
  IconCalendar,
  IconCheck,
  IconX
} from "@tabler/icons-react";

export default function LaporanMonitoringPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // State Modal
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // State Form
  const [form, setForm] = useState({
    hasil_monitoring: "",
    tanggal_monitoring: new Date()
  });

  // --- 1. FETCH DATA ---
  const fetchData = async () => {
    setLoading(true);
    try {
      // Ambil data distribusi (sudah include laporanMonitoring)
      const response = await api.get("/supervisor/distribusi"); 
      
      const hasil = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      
      setData(hasil);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 2. HANDLER MODAL ---
  const handleOpenModal = (item) => {
    setSelectedItem(item);
    
    if (item.laporan_monitoring) {
       // --- MODE EDIT ---
       setIsEditMode(true);
       setForm({
         hasil_monitoring: item.laporan_monitoring.hasil_monitoring,
         tanggal_monitoring: new Date(item.laporan_monitoring.tanggal_monitoring)
       });
    } else {
       // --- MODE BARU ---
       setIsEditMode(false);
       setForm({ 
           hasil_monitoring: "", 
           tanggal_monitoring: new Date() 
       });
    }
    open();
  };

  // --- 3. SUBMIT DATA ---
  const handleSubmit = async () => {
    if (!form.hasil_monitoring || !form.tanggal_monitoring) {
        alert("Mohon lengkapi data laporan.");
        return;
    }

    setSubmitting(true);
    try {
      // Format tanggal YYYY-MM-DD
      const payload = {
          ...form,
          tanggal_monitoring: form.tanggal_monitoring.toLocaleDateString('en-CA')
      };

      if (isEditMode) {
        // Update
        const laporanId = selectedItem.laporan_monitoring.id;
        await api.put(`/supervisor/laporan-monitoring/${laporanId}`, payload);
      } else {
        // Create
        await api.post(`/supervisor/distribusi/${selectedItem.id}/monitoring`, payload);
      }

      alert("Laporan Monitoring berhasil disimpan!");
      close();
      fetchData();
    } catch (error) {
      console.error(error);
      alert("Gagal menyimpan laporan.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- 4. RENDER UI ---
  if (loading) return <Center h={300}><Loader size="lg" /></Center>;

  const rows = data.map((item) => {
    const hasReport = !!item.laporan_monitoring;

    return (
      <Table.Tr key={item.id}>
        {/* Tanggal Distribusi */}
        <Table.Td>
          <Text fz="sm" fw={500}>
            {new Date(item.tanggal_distribusi).toLocaleDateString('id-ID', {
                day: 'numeric', month: 'short', year: 'numeric'
            })}
          </Text>
        </Table.Td>

        {/* Detail Distribusi */}
        <Table.Td>
           <Group gap="sm">
             <ThemeIcon variant="light" color="blue" size="lg" radius="xl">
                <IconBuilding size={18}/>
             </ThemeIcon>
             <div>
                <Text fz="sm" fw={500}>{item.sekolah?.nama_sekolah || "Sekolah Dihapus"}</Text>
                <Group gap={5}>
                    <IconChefHat size={12} color="gray"/>
                    <Text fz="xs" c="dimmed">{item.menu?.nama_menu || "Menu Dihapus"}</Text>
                </Group>
             </div>
           </Group>
        </Table.Td>

        {/* Status Laporan */}
        <Table.Td>
           {hasReport ? (
             <Badge color="teal" variant="light" leftSection={<IconCheck size={12}/>}>
                Selesai
             </Badge>
           ) : (
             <Badge color="gray" variant="outline" leftSection={<IconX size={12}/>}>
                Belum Ada
             </Badge>
           )}
        </Table.Td>

        {/* Hasil Monitoring (Preview) */}
        <Table.Td>
           <Text fz="sm" lineClamp={1} c={hasReport ? "dark" : "dimmed"} fs={hasReport ? "normal" : "italic"}>
              {hasReport ? item.laporan_monitoring.hasil_monitoring : "- Belum diisi -"}
           </Text>
        </Table.Td>

        {/* Aksi */}
        <Table.Td>
           <Group gap="xs" justify="flex-end">
             <Tooltip label={hasReport ? "Edit Laporan" : "Buat Laporan"}>
                <ActionIcon 
                    variant={hasReport ? "subtle" : "filled"} 
                    color="teal"
                    onClick={() => handleOpenModal(item)}
                >
                    {hasReport ? <IconPencil size={16}/> : <IconPlus size={16}/>}
                </ActionIcon>
             </Tooltip>
           </Group>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div style={{ padding: 20 }}>
      <Group justify="space-between" mb="lg">
        <div>
           <Title order={2}>Laporan Monitoring</Title>
           <Text c="dimmed" size="sm">Evaluasi kebersihan dan ketepatan distribusi</Text>
        </div>
        <Button variant="light" onClick={fetchData} leftSection={<IconRefresh size={16}/>}>
            Refresh
        </Button>
      </Group>

      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        <ScrollArea>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th>Tgl Distribusi</Table.Th>
                <Table.Th>Sekolah & Menu</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Hasil Monitoring</Table.Th>
                <Table.Th ta="right">Aksi</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
               {rows.length > 0 ? rows : (
                  <Table.Tr><Table.Td colSpan={5} align="center" py="xl" c="dimmed">Belum ada data distribusi.</Table.Td></Table.Tr>
               )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* --- MODAL FORM --- */}
      <Modal 
        opened={opened} 
        onClose={close} 
        title={
            <Group gap={10}>
                <ThemeIcon color="teal" variant="light"><IconClipboardCheck size={18}/></ThemeIcon>
                <Text fw={700}>{isEditMode ? "Edit Laporan Monitoring" : "Input Laporan Baru"}</Text>
            </Group>
        }
        centered
        size="lg"
      >
        <Box>
           {/* Header Info */}
           {selectedItem && (
             <Paper withBorder p="sm" bg="teal.0" mb="md" radius="md">
                <Group justify="space-between">
                    <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Lokasi Sekolah</Text>
                        <Text fw={500}>{selectedItem.sekolah?.nama_sekolah}</Text>
                    </div>
                    <div style={{textAlign:'right'}}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Menu Makanan</Text>
                        <Text fw={500}>{selectedItem.menu?.nama_menu}</Text>
                    </div>
                </Group>
             </Paper>
           )}

           <DateInput
              label="Tanggal Monitoring"
              placeholder="Pilih tanggal"
              value={form.tanggal_monitoring}
              onChange={(val) => setForm({...form, tanggal_monitoring: val})}
              mb="md"
              leftSection={<IconCalendar size={16}/>}
              withAsterisk
           />

           <Textarea
              label="Hasil Monitoring / Evaluasi"
              placeholder="Contoh: Distribusi tepat waktu, kemasan rapi, makanan dalam kondisi baik..."
              minRows={4}
              mb="lg"
              value={form.hasil_monitoring}
              onChange={(e) => setForm({...form, hasil_monitoring: e.target.value})}
              withAsterisk
           />

           <Group justify="flex-end">
              <Button variant="default" onClick={close}>Batal</Button>
              <Button onClick={handleSubmit} loading={submitting} color="teal" leftSection={<IconCheck size={16}/>}>
                  Simpan Laporan
              </Button>
           </Group>
        </Box>
      </Modal>
    </div>
  );
}